import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, distinctUntilChanged, map, tap } from 'rxjs';
import { ApiService } from '../../api.service';
import { QueryParams } from '../../core/enums/query-params.enum';
import { SearchParamsInterface } from '../../core/models/search-params.model';
import { SortInterface } from '../../core/models/sort.model';
import { QueryParamsService } from '../../core/services/query-params/query-params.service';
import { Search } from '../../core/tree/search/search.enum';
import {
  SearchableListParentSearchResponseInterface,
  SearchableListParentSearchService,
} from '../../inquiries/searchable-list-parent-search.service';

@Injectable()
export class ManagedUsersSearchService extends SearchableListParentSearchService implements OnDestroy {
  protected api: ApiService;
  protected translator: TranslateService;
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  placeholder = 'SEARCH.PLACEHOLDER';
  protected endpointUrl = (term) => `company-users/`;

  term$ = this.activatedRoute.queryParams.pipe(
    map((params) => params[QueryParams.TERM] ?? null),
    distinctUntilChanged(),
  );
  searchTerm: string;
  searchActive = false;
  searchActive$ = this.term$.pipe(
    map((term) => {
      this.searchTerm = term;
      return term && term.length > Search.MIN_TERM_LENGTH;
    }),
    distinctUntilChanged(),
    tap((isActive) => (this.searchActive = isActive)),
  );

  private subscription = new Subscription();
  private allowedQueryParams: QueryParams[] = [QueryParams.TERM];

  constructor() {
    super();

    this.subscription.add(this.searchActive$.subscribe());
  }

  changeTerm(term: string | null): void {
    if (term && term.length > Search.MIN_TERM_LENGTH) {
      const currentParams = QueryParamsService.getAllowedQueryParams(this.activatedRoute, this.allowedQueryParams);
      this.router.navigate([], {
        queryParams: { ...currentParams, term },
        replaceUrl: this.isSearchActive(),
        relativeTo: this.activatedRoute,
      });
    } else if (!term && this.isSearchActive()) {
      this.restorePreSearchNavState();
    }
  }

  isSearchActive(): boolean {
    return this.searchActive;
  }

  get(
    term: string,
    sort: SortInterface,
    page: number,
    filter: SearchParamsInterface,
  ): Observable<SearchableListParentSearchResponseInterface> {
    const params = { search: term, ...filter };

    return this.api
      .get(this.endpointUrl(term), params)
      .noCache()
      .pipe(map(({ data, meta }) => ({ data, meta, query: term })));
  }

  restorePreSearchNavState() {
    this.router.navigate([], { queryParams: { [QueryParams.TERM]: null } });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
